import React, { useRef, useEffect } from "react";
import { motion, useAnimation, useInView } from "framer-motion";
import { useLanguage } from "../../context/LanguageContext";

const easeOutBezier: [number, number, number, number] = [0.16, 1, 0.3, 1];

const VisionMission: React.FC<{ content?: any }> = ({ content }) => {
  const { t_api } = useLanguage();
  const introTitle = t_api(content?.intro_title || "About • VISION & MISSION", content?.intro_title_ar);
  const title = t_api(content?.title || "Vision & Mission", content?.title_ar);
  const visionTitle = t_api(content?.vision_title || "Our Vision", content?.vision_title_ar);
  const visionDesc = t_api(content?.vision_desc || "Hamdan Trading Group will strive with all its strength and capabilities to provide products and services of outstanding quality which far exceed expectations of our clients and make us their preferred choice.", content?.vision_desc_ar);
  const missionTitle = t_api(content?.mission_title || "Our Mission", content?.mission_title_ar);
  const missionDesc = t_api(content?.mission_desc || "As the world steps into a new era of development, it's the duty of each organization to be proactive to the environment and society as well as adapt to the new changes. At Hamdan Trading Group it's our firm endeavor to contribute towards the betterment of society whilst efficiently utilizing our environmental resources.\n\nWith our wide spectrum of services on offer, we showcase our commitment to serving modern society in the best possible manner. At Hamdan Trading Group, we strive to live up to our high standards. Hence, our products and services have a distinct mark of quality attached and reflect our philosophy. Our ultimate aim is to provide the best services in the region with a complete value for money.", content?.mission_desc_ar);
  const image = content?.image_1 ? (content?.image_1.startsWith('http') ? content.image_1 : `https://hamdanoman.com/${content.image_1}`) : "/our-Mission-Vision.jpg";
  const guidingTitle = t_api(content?.guiding_title || "Guiding Principles", content?.guiding_title_ar);
  const guidingDesc = t_api(content?.guiding_desc || "Quality, responsibility, innovation, and value guide every decision we make at Hamdan Trading Group.", content?.guiding_desc_ar);

  const ref = useRef<HTMLDivElement | null>(null);

  // ✅ Detect when section comes into view
  const isInView = useInView(ref, { amount: 0.25, once: true });

  // ✅ Controls animation start
  const controls = useAnimation();

  useEffect(() => {
    if (isInView) controls.start("show");
  }, [isInView, controls]);

  const header = {
    hidden: { opacity: 0, y: -18 },
    show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: easeOutBezier } },
  };

  const line = {
    hidden: { scaleY: 0 },
    show: { scaleY: 1, transition: { duration: 0.9, ease: easeOutBezier } },
  };

  const leftBlock = {
    hidden: { opacity: 0, x: -35 },
    show: { opacity: 1, x: 0, transition: { duration: 0.65, ease: easeOutBezier } },
  };

  const rightBlock = {
    hidden: { opacity: 0, x: 35, scale: 0.98 },
    show: { opacity: 1, x: 0, scale: 1, transition: { duration: 0.75, ease: easeOutBezier } },
  };

  const fadeUp = {
    hidden: { opacity: 0, y: 18 },
    show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: easeOutBezier } },
  };

  const dot1 = {
    hidden: { opacity: 0, scale: 0.6 },
    show: { opacity: 1, scale: 1, transition: { duration: 0.4, ease: easeOutBezier } },
  };

  const dot2 = {
    hidden: { opacity: 0, scale: 0.6 },
    show: { opacity: 1, scale: 1, transition: { duration: 0.4, delay: 0.05, ease: easeOutBezier } },
  };

  return (
    <section className="bg-white">
      {/* ✅ this ref is important */}
      <div ref={ref} className="max-w-7xl mx-auto px-6 py-10">
        {/* Header */}
        <motion.div
          variants={header}
          initial="hidden"
          animate={controls}
          className="mb-12"
        >
          <p className="text-xs font-semibold tracking-widest text-red-500 uppercase">
            {introTitle}
          </p>
          <h1 className="mt-4 text-2xl md:text-3xl font-extrabold text-[#0B1220]">
            {title}
          </h1>
        </motion.div>

        <div className="grid lg:grid-cols-12 gap-12 items-start">
          {/* LEFT */}
          <div className="lg:col-span-7 relative">
            {/* Vertical line */}
            <motion.div
              variants={line}
              initial="hidden"
              animate={controls}
              className="absolute left-4 top-0 bottom-0 w-[2px] bg-gray-200 origin-top"
            />

            {/* Vision */}
            <motion.div
              variants={leftBlock}
              initial="hidden"
              animate={controls}
              className="relative pl-14 mb-12"
            >
              <motion.span
                variants={dot1}
                initial="hidden"
                animate={controls}
                className="absolute left-0 top-1 h-8 w-8 rounded-full bg-[#F2BB13] flex items-center justify-center font-extrabold text-[#0B1220]"
              >
                1
              </motion.span>

              <h2 className="text-xl font-extrabold text-gray-900 mb-3">{visionTitle}</h2>

              <div className="text-gray-700 leading-relaxed text-justify text-[15px] sm:text-sm md:text-base">
                <div dangerouslySetInnerHTML={{ __html: visionDesc.replace(/\n/g, '<br/>') }} />
              </div>
            </motion.div>

            {/* Mission */}
            <motion.div
              variants={leftBlock}
              initial="hidden"
              animate={controls}
              className="relative pl-14"
              transition={{ delay: 0.08 }}
            >
              <motion.span
                variants={dot2}
                initial="hidden"
                animate={controls}
                className="absolute left-0 top-1 h-8 w-8 rounded-full bg-[#C1121F] flex items-center justify-center font-extrabold text-white"
              >
                2
              </motion.span>

              <h2 className="text-xl font-extrabold text-gray-900 mb-3">{missionTitle}</h2>

              <div className="space-y-4 text-gray-700 leading-relaxed text-justify text-[15px] sm:text-sm md:text-base">
                <div dangerouslySetInnerHTML={{ __html: missionDesc.replace(/\n/g, '<br/>') }} />
              </div>
            </motion.div>
          </div>

          {/* RIGHT */}
          <motion.div
            variants={rightBlock}
            initial="hidden"
            animate={controls}
            className="lg:col-span-5"
          >
            <div className="rounded-3xl overflow-hidden border border-gray-200 bg-slate-50 shadow-sm aspect-video flex items-center justify-center">
              <img
                src={image}
                alt={title}
                className="w-full h-full object-contain"
              />
            </div>

            <motion.div
              variants={fadeUp}
              initial="hidden"
              animate={controls}
              className="mt-4 rounded-2xl bg-gray-50 p-5 border border-gray-200"
            >
              <p className="text-xl font-semibold text-[#0B1220]">
                {guidingTitle}
              </p>
              <p className="mt-2 text-[15px] sm:text-sm md:text-base text-gray-600 leading-relaxed">
                {guidingDesc}
              </p>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default VisionMission;
